"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Search, ShieldCheck, Zap, FileCheck, Heart, Mail, ArrowRight } from "lucide-react";
import PhoneMockup from "./PhoneMockup";

/** Market-test: real-photo hero vs graphic hero (market-test-plan.md §3). */
const HERO_EXPERIMENT_ID = "wf_photo_hero_vs_graphic_v1";
const HERO_VARIANT_ID = "photo";

/**
 * Travel Connected (photo-commerce) hero — editorial PHOTO-led commercial hero
 * per generated_homepage_or_landing.png: real travel photograph + world-map
 * connection lines + floating phone mockup, with a destination SEARCH as the
 * primary action ("Find my eSIM"). White UI, bold black headline, bright blue
 * CTA. Trust chips are CLAIM-SAFE (no ratings/Trustpilot/review counts).
 */
export default function PhotoHero() {
  const { t } = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");

  // Hero-variant exposure for the photo-vs-graphic hero test (best-effort,
  // consent-gated; `track` dedupes). market-test-plan.md §3/§10.
  useEffect(() => {
    try {
      track({
        event_name: "hero_variant_viewed",
        event_id: `hero_variant_viewed:${HERO_EXPERIMENT_ID}:${HERO_VARIANT_ID}`,
        experiment_id: HERO_EXPERIMENT_ID,
        variant_id: HERO_VARIANT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      track({
        event_name: "destination_search_submitted",
        event_id: `destination_search_submitted:${HERO_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: HERO_EXPERIMENT_ID,
        variant_id: HERO_VARIANT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
    router.push("/destinations/");
  };

  const chips = [
    { icon: ShieldCheck, label: t("pc.hero.chip1", "Secure checkout") },
    { icon: Zap, label: t("pc.hero.chip2", "Instant activation") },
    { icon: FileCheck, label: t("pc.hero.chip3", "No contract") },
    { icon: Heart, label: t("pc.hero.chip4", "Loved by travelers") },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="container mx-auto px-4 pt-10 pb-12 lg:pt-16 lg:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        {/* Copy + search */}
        <div className="relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            {t("pc.hero.title", "Travel connected in minutes.")}
          </h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-xl">
            {t(
              "pc.hero.subtitle",
              "Choose your destination, get your eSIM instantly, and avoid roaming surprises wherever you go.",
            )}
          </p>

          <form onSubmit={onSubmit} className="mt-7 max-w-xl">
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              {t("pc.hero.searchLabel", "Where are you traveling to?")}
            </label>
            <div className="pc-card flex items-center gap-2 p-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  type="text"
                  placeholder={t("pc.hero.searchPlaceholder", "E.g., Japan, United Kingdom, USA")}
                  aria-label={t("pc.hero.searchLabel", "Where are you traveling to?")}
                  className="w-full h-11 rounded-lg bg-transparent pl-10 pr-3 text-foreground focus:outline-none"
                />
              </div>
              <button
                type="submit"
                aria-label={t("pc.hero.searchCta", "Find my eSIM")}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary text-primary-foreground shrink-0 hover:bg-primary/90 transition-all active:scale-[0.96]"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </form>

          <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="w-4 h-4 text-primary" />
            {t("pc.hero.delivery", "Instant delivery to your email.")}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => router.push("/destinations/")}
              className="inline-flex items-center gap-2 h-12 rounded-full bg-primary text-primary-foreground px-7 font-bold shadow-sm hover:bg-primary/90 transition-all active:scale-[0.98] hover:shadow-md group/cta"
            >
              {t("pc.hero.cta", "Find my eSIM")} <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={() => router.push("/installation-guide/")}
              className="text-sm font-bold text-foreground underline underline-offset-4 hover:text-primary"
            >
              {t("pc.hero.secondary", "How it works")}
            </button>
          </div>

          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
            {chips.map(({ icon: Icon, label }) => (
              <li key={label} className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80">
                <Icon className="w-4 h-4 text-primary" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Photo + phone mockup */}
        <div className="group relative w-full aspect-[4/5] sm:aspect-square lg:aspect-[4/5] mt-8 lg:mt-0 max-w-[500px] ml-auto">
          {/* Main Photo container */}
          <div className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5 bg-slate-100">
            <ExportedImage
              src="/images/brands/photo-commerce-travel-connected/photo_hero_bg.webp"
              alt={t("pc.hero.bgAlt", "Traveler outdoors in a European city")}
              fill
              priority
              className="object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            />
            
            {/* Subtle wash */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent pointer-events-none" />
            
            {/* world-map connection motif (solid, no blend mode) */}
            <svg className="absolute inset-0 w-full h-full opacity-40 z-10" viewBox="0 0 400 300" fill="none" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
              <path d="M40 210 C 140 60, 280 60, 360 150" stroke="white" strokeWidth="2" strokeDasharray="4 6" />
              <circle cx="40" cy="210" r="5" fill="white" />
              <circle cx="360" cy="150" r="5" fill="white" />
              <circle cx="200" cy="95" r="4" fill="white" className="animate-pulse" />
            </svg>
          </div>

          {/* floating phone mockup */}
          <div className="absolute -left-2 bottom-4 sm:-left-4 sm:bottom-0 lg:-left-12 lg:-bottom-6 w-[150px] sm:w-[170px] lg:w-[220px] z-20 animate-float drop-shadow-[0_20px_30px_rgba(0,0,0,0.25)]">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
